/**
 * StatusFilterDropdown — the Production Tracker "Committed" tab's production-status
 * filter (BLU-811).
 *
 * A thin shadcn `Select`, single-select, in the `production` namespace. Deliberately
 * NOT built on `ClientFilterDropdown`: that one is welded to the `inventory` ns, the
 * `ALL_CLIENTS` sentinel and a >6-option typeahead. The status set is small and fixed
 * (a handful of production stages), so a plain Select with no typeahead is the right
 * shape here.
 *
 * Options come ENTIRELY from `statusBreakdown` keys — that facet already carries the
 * `NOT_IN_PRODUCTION` bucket as a key when any order lacks live production, so there is
 * no separately-appended "Not in production" entry. Order follows the shared
 * `STATUS_RANK` (least-advanced first, `NOT_IN_PRODUCTION` last) so it matches the
 * server ladder and the header breakdown tooltip. Each option carries its facet count,
 * e.g. "Queued (12)"; the "All statuses (N)" row sums the facet.
 *
 * `value === undefined` means "all statuses". A local sentinel stands in for that in
 * the Select (Radix forbids an empty-string item value), mapped back to `undefined` on
 * change so the caller never has to know about it.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ListFilter } from 'lucide-react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  STATUS_LABEL_KEYS,
  NOT_IN_PRODUCTION,
  sortStatusKeysByRank,
} from '../lib/order-status-display';
import type { CommittedProductionStatus } from '../lib/order-status-display';

// Radix Select rejects an empty-string item value, so "all" needs a sentinel. Local to
// this component — the caller only ever sees `undefined` for "all".
const ALL_STATUSES = '__all_statuses__';

export interface StatusFilterDropdownProps {
  /** Selected production status, or `undefined` for "all statuses". */
  value: CommittedProductionStatus | undefined;
  onChange: (next: CommittedProductionStatus | undefined) => void;
  /** Per-status order counts — the facet. Drives the option list and its counts. */
  statusBreakdown: Record<string, number>;
}

export function StatusFilterDropdown({
  value,
  onChange,
  statusBreakdown,
}: StatusFilterDropdownProps) {
  const { t } = useTranslation('production');

  const options = useMemo(
    () => sortStatusKeysByRank(Object.keys(statusBreakdown)),
    [statusBreakdown],
  );
  const total = useMemo(
    () => Object.values(statusBreakdown).reduce((sum, n) => sum + n, 0),
    [statusBreakdown],
  );

  const labelFor = (status: string) => {
    const key =
      status === NOT_IN_PRODUCTION
        ? 'tracker.committed.notInProduction'
        : STATUS_LABEL_KEYS[status];
    return key ? t(key) : status.replace(/_/g, ' ');
  };

  return (
    <Select
      value={value ?? ALL_STATUSES}
      onValueChange={(next) =>
        onChange(next === ALL_STATUSES ? undefined : (next as CommittedProductionStatus))
      }
    >
      <SelectTrigger
        className="w-[200px] gap-2 h-8"
        aria-label={t('tracker.committed.statusFilter.ariaLabel')}
      >
        <ListFilter
          className="h-3.5 w-3.5 text-muted-foreground shrink-0"
          aria-hidden="true"
        />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_STATUSES}>
          {t('tracker.committed.statusFilter.allStatuses', { count: total })}
        </SelectItem>
        {options.map((status) => (
          <SelectItem key={status} value={status}>
            {t('tracker.committed.statusFilter.option', {
              label: labelFor(status),
              count: statusBreakdown[status] ?? 0,
            })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
